import Typography from "@material-ui/core/Typography";
import React, { FC, useMemo, useState } from "react";
import { Button } from "@material-ui/core";
import { DropzoneArea } from "material-ui-dropzone";
import { useSelector } from "react-redux";

import LoaadedAsset from "./LoadedAsset";
import classes from "../AdGroupCreationForm.module.scss";
import { CreativeAssetFromFile } from "../../../../../../types/creativeTypes";
import { InvalidDroppedFile, ValidDroppedFile } from "../types";
import { ReduxApplicationState } from "../../../../../../types/ReduxTypes/ReduxApplicationState";
import { errorToaster, successToaster } from "../../../../../../helpers/toasters";
import { handleImageLoad, handleVideoLoad, validateFileType } from "../helpers";
import { invalidFileTypeImageErrorMessage, invalidFileTypeVideoErrorMessage, videoDuration } from "../constants";

const uniqBy = (arr: any, selector = (item: any) => item) => {
  const map = new Map();
  arr.forEach((item: any) => {
    const prop = selector(item);
    if (!map.has(prop)) map.set(prop, item);
  });
  return [...map.values()];
};

type AttachAssetsStepProps = {
  creativeAssets: CreativeAssetFromFile[];
  setFieldValue: (fieldName: string, value: CreativeAssetFromFile[]) => void;
  limit: number;
  isFromPage?: boolean;
  handleBtnSave?: () => void;
  assetsListTitle?: string;
};

const AttachAssetsStep: FC<AttachAssetsStepProps> = ({
  creativeAssets,
  setFieldValue,
  limit,
  isFromPage = false,
  handleBtnSave = null,
  assetsListTitle = "Uploaded",
}) => {
  const [assetsToPreviewIndexes, setAssetsToPreviewIndexes] = useState<number[]>([]);
  const [invalidCreativeAssets, setInvalidCreativeAssets] = useState<InvalidDroppedFile[]>([]);
  const isVideoCampaign = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.basicDetails.isVideoCampaign,
  );

  const files = useMemo(() => {
    return creativeAssets.map((creativeAsset) => creativeAsset.file);
  }, [creativeAssets]);

  const handleDrop = async (droppedFiles: File[]) => {
    const filesToSet = [...files, ...droppedFiles];
    const uniqFiles = uniqBy(filesToSet, (file: File) => file.name);
    const validFiles: ValidDroppedFile[] = [];
    const invalidFiles: InvalidDroppedFile[] = [];

    const filesWithInvalidTypes = droppedFiles.filter((file: File) => !validateFileType(file, isVideoCampaign));

    if (filesWithInvalidTypes.length !== 0) {
      errorToaster(isVideoCampaign ? invalidFileTypeImageErrorMessage : invalidFileTypeVideoErrorMessage, 15000);
    }

    const loadFile = (file: File) => {
      return new Promise<void>((resolve, reject) => {
        const fileType = file.type.split("/")[0];

        // validate file type
        if (validateFileType(file, isVideoCampaign)) {
          if (fileType === "image") {
            handleImageLoad(file, validFiles, invalidFiles, resolve, isVideoCampaign);
          } else if (fileType === "video") {
            handleVideoLoad(file, validFiles, resolve);
          } else {
            reject();
          }
        } else {
          reject();
        }
      });
    };

    await Promise.allSettled(uniqFiles.map((file: File) => loadFile(file)));

    if (invalidFiles.length !== 0) {
      // eslint-disable-next-line no-shadow
      setInvalidCreativeAssets(invalidFiles);
      invalidFiles.forEach((invalidFile) => {
        errorToaster(<p>{invalidFile.errorMessage}</p>, 15000);
      });
      if (validFiles.length !== 0) {
        successToaster("Rest Files are Successfully Loaded", 10000, "bottom-center");
        const assets = validFiles.map((validFile) => ({
          file: validFile.file,
          width: validFile.width,
          height: validFile.height,
          fileType: validFile.fileType,
        }));
        setFieldValue("creativeAssets", assets);
      }
    } else if (validFiles.length !== 0) {
      successToaster("Successfully Loaded Files", 10000, "bottom-center");
      const assets = validFiles.map((validFile) => ({
        file: validFile.file,
        width: validFile.width,
        height: validFile.height,
        fileType: validFile.fileType,
        duration: validFile.duration,
      }));
      setFieldValue("creativeAssets", assets);
    }
  };

  const handleRemoveAsset = (fileIndexToRemove: number) => () => {
    const updatedCreativeAssets = creativeAssets.filter(
      (_, creativeAssetIndex) => creativeAssetIndex !== fileIndexToRemove,
    );
    setFieldValue("creativeAssets", updatedCreativeAssets);
  };

  const handleShowAsset = (creativeAssetIndex: number) => () => {
    setAssetsToPreviewIndexes((prevFileToPreviewIndexes: number[]) => [
      ...prevFileToPreviewIndexes,
      creativeAssetIndex,
    ]);
  };

  const handleHideAsset = (creativeAssetIndex: number) => () => {
    setAssetsToPreviewIndexes((prevFileToPreviewIndexes: number[]) => {
      return prevFileToPreviewIndexes.filter((fIndex) => fIndex !== creativeAssetIndex);
    });
  };

  return (
    <div>
      <DropzoneArea
        acceptedFiles={isVideoCampaign ? ["video/mp4"] : ["image/*"]}
        dropzoneText="Choose files to upload for this campaign's ad group"
        dropzoneClass={classes.dropzoneArea}
        filesLimit={limit}
        initialFiles={files}
        onDrop={handleDrop}
        previewText="Selected files"
        showPreviews={false}
        showAlerts={false}
        maxFileSize={isVideoCampaign ? 250000000 : 800000}
        previewGridClasses={{
          container: classes.dropzoneAreaChips,
        }}
        showPreviewsInDropzone={false}
      />

      <Typography className={classes.dropzoneHelperText}>Max Video Size is: 250MB</Typography>
      <Typography className={classes.dropzoneHelperText} style={{ marginTop: "0" }}>
        Max Image Size is: 800KB
      </Typography>

      <Typography className={classes.dropzoneHelperText}>
        We have found the most success with the following ad sizes (images/gifs):{" "}
        <b>
          1024x768, 970x250, 970x90, 768x1024, 728x90, 480x320, 468x60, 336x280, 320x480, 320x100, 320x50, 300x1050,
          300x600, 300x250, 800x1100, 300x50, 250x250, 216x36, 200x200, 180x150, 168x28, 160x600, 120x600, 120x240,
          120x60, 120x20.
        </b>{" "}
        Using as many ad sizes on this list as possible increases the likelihood of having an opportunity to bid in more
        cases, which can help to increase the reach of tightly targeted campaigns.
        <br />
        <br />
        <a
          href="https://www.brandgen.io/hubfs/Help%20Documents/BrandGen%20Ad%20Specifications.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          BrandGen Ad Sizes Recommendations
        </a>
      </Typography>

      {creativeAssets.length !== 0 ? (
        <div className={classes.uploadedFilesContainer}>
          <Typography variant="h3" align="left">
            {assetsListTitle}
          </Typography>

          {invalidCreativeAssets.length !== 0 ? (
            <>
              <Typography variant="h4" className={classes.assetsPackStatus}>
                Invalid Assets:{" "}
              </Typography>

              {invalidCreativeAssets.map(
                (invalidCreativeAsset: CreativeAssetFromFile, invalidCreativeAssetIndex: number) => (
                  <LoaadedAsset
                    key={invalidCreativeAsset.file.name}
                    creativeAsset={invalidCreativeAsset}
                    isAssetShown={assetsToPreviewIndexes.includes(-invalidCreativeAssetIndex - 1)}
                    handleHideAsset={handleHideAsset(-invalidCreativeAssetIndex - 1)}
                    handleShowAsset={handleShowAsset(-invalidCreativeAssetIndex - 1)}
                    invalid
                  />
                ),
              )}
            </>
          ) : null}

          <>
            {invalidCreativeAssets.length !== 0 ? (
              <Typography variant="h4" className={classes.assetsPackStatus}>
                Valid Assets:{" "}
              </Typography>
            ) : null}

            {creativeAssets.map((creativeAsset: CreativeAssetFromFile, creativeAssetIndex: number) => (
              <div
                key={creativeAsset.file.name}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                <div style={{ width: "100%" }}>
                  <LoaadedAsset
                    key={creativeAsset.file.name}
                    creativeAsset={creativeAsset}
                    isAssetShown={assetsToPreviewIndexes.includes(creativeAssetIndex)}
                    handleHideAsset={handleHideAsset(creativeAssetIndex)}
                    handleShowAsset={handleShowAsset(creativeAssetIndex)}
                    handleRemoveAsset={handleRemoveAsset(creativeAssetIndex)}
                  />
                </div>

                {isVideoCampaign && !videoDuration.includes(creativeAsset.duration!) && (
                  <Typography style={{ fontSize: 13, marginTop: "8px" }}>
                    The video creative has an unusual duration - {creativeAsset.duration} seconds. The most common
                    durations are 15 and 30 seconds.
                  </Typography>
                )}
              </div>
            ))}
          </>
          {isFromPage && (
            <Button className={classes.btnSave} onClick={handleBtnSave!}>
              Submit
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AttachAssetsStep;
